import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Box,
  FormControl,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import Loading from "./Loading";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import FormatDateTime from "../../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import { useTranslation } from "react-i18next";
import EditClient from "./EditClient";
import ExportToPDF from "./export/ExportToPDF";
import debounce from "lodash.debounce";
import { getAccess } from "../../../../permissions/Permissions";

const tableCellStyle = {
  textAlign: "center",
};

const statusColors = {
  14: "#ffebee", // Coming_Soon: light red
  13: "#ffccbc", // No_Answer: light orange
  12: "#e8f5e9", // New: light green
  11: "#fff9c4", // Busy: light yellow
  10: "#f3e5f5", // In_Correct_No: light purple
  9: "#e8eaf6", // Whatsapp: light indigo
  8: "#fce4ec", // Disturb: light pink
  7: "#e1f5fe", // Information: light cyan
  6: "#f1f8e9", // Phone_Off: light lime
  5: "#e0f2f1", // Fixed: light teal
  4: "#ffecb3", // Dont Want: light amber
  3: "#dcedc8", // Done: light green
  2: "#e0f7fa", // Appointment: light cyan
  1: "#d1c4e9", // FollowUp: light purple
};

const ClientList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const permissions = getAccess(user?.user?.permissions, user?.user?.isAdmin);
  const token = user?.user?.token || user?.token;
  const [openEdit, setOpenEdit] = useState(null);
  const [createdAt, setCreatedAt] = useState(""); // New created_at filter
  // Compute default dates:
  // - For privileged users (admin, call center, manager): set to current day.
  // - Otherwise, set to the current month (from 1st to last day).
  const defaultStartDate = useMemo(() => {
    const today = new Date();
    if (
      permissions?.is_staff ||
      permissions?.call_center_admin ||
      permissions?.is_manager_in_call_center
    ) {
      return today.toISOString().split("T")[0];
    } else {
      const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return startMonth.toISOString().split("T")[0];
    }
  }, [permissions]);

  const defaultEndDate = useMemo(() => {
    const today = new Date();
    if (
      permissions?.is_staff ||
      permissions?.call_center_admin ||
      permissions?.is_manager_in_call_center
    ) {
      console.log("is this condition work?");
      return today.toISOString().split("T")[0];
    } else {
      const endMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return endMonth.toISOString().split("T")[0];
    }
  }, [permissions]);

  // Initialize date filters with defaults.
  const [startCreatedAt, setStartCreatedAt] = useState(defaultStartDate);
  const [endCreatedAt, setEndCreatedAt] = useState(defaultEndDate);

  // If defaults change, update the state.
  useEffect(() => {
    setStartCreatedAt(defaultStartDate);
    setEndCreatedAt(defaultEndDate);
    if (defaultStartDate && defaultEndDate) {
      console.log("Auto-set dates:", defaultStartDate, defaultEndDate);
    } else {
      console.log("No defaults available");
    }
  }, [defaultStartDate, defaultEndDate]);

  const [editData, setEditData] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusID, setStatusID] = useState("");
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(null);
  const [total, setTotal] = useState("");

  // Pagination and ID filter.
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalClient, setTotalClient] = useState(0);
  const [id, setID] = useState("");

  // Debounce keyword update.
  const debouncedSetKeyword = useMemo(
    () => debounce((value) => setKeyword(value), 500),
    []
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debouncedSetKeyword(e.target.value);
  };

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  // Build the API URL including the date filters.
  const apiUrl = `client/?search=${keyword}&status=${statusID}&id=${id}&page=${page}&page_size=${rowsPerPage}&created_at=${createdAt}`;

  const {
    data: clients,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(["clients"], apiUrl, token);

  const { data: status } = useFetchObjects(["status"], "status", token);

  useEffect(() => {
    refetch();
  }, [
    keyword,
    statusID,
    startCreatedAt,
    endCreatedAt,
    id,
    page,
    rowsPerPage,
    createdAt,
    refetch,
  ]);

  useEffect(() => {
    if (clients && typeof clients.count !== "undefined") {
      setTotalClient(clients.count);
    }
  }, [clients]);

  useEffect(() => {
    setTotal(clients?.results?.length);
  }, [clients]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleOpenDialog = () => {
    setDescription(null);
    setOpen((prev) => !prev);
  };

  // Define columns for PDF export.
  const exportColumns = [
    { label: t("call_center.id"), id: "id" },
    { label: t("call_center.name"), id: "full_name" },
    { label: t("call_center.contact"), id: "contact" },
    { label: t("call_center.procedure"), id: "procedure_name" },
    { label: t("call_center.procedureDate"), id: "procedure_date" },
    { label: t("call_center.status"), id: "status" },
    { label: t("call_center.price"), id: "price" },
    { label: t("call_center.by"), id: "by" },
    { label: t("call_center.description"), id: "description" },
  ];

  // Prepare data for PDF export.
  const exportData = clients?.results?.map((client) => ({
    id: client.id,
    full_name: client.full_name,
    contact: client.contact,
    procedure_name: client?.display_procedure?.name,
    procedure_date: client?.procedure_date,
    status: client?.status_info?.name,
    price: client.price,
    by: `${client?.display_user?.first_name} ${client?.display_user?.last_name}`,
    description: client?.description || "",
  }));

  // Helper function to set row background color based on client's status.
  const getRowStyle = (client) => {
    if (client?.status_info?.id) {
      return {
        backgroundColor: statusColors[client.status_info.id] || "inherit",
      };
    }
    return {};
  };

  return (
    <Grid container justifyContent={"center"}>
      <Paper sx={{ padding: "15px", width: "100%", mt: 2 }}>
        <Grid item xs={12} md={11.5}>
          <Grid
            container
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography
              variant="h5"
              fontWeight={"bold"}
              align="center"
              sx={{ padding: 2 }}
            >
              {t("call_center.clientsList")}
            </Typography>
          </Grid>

          {/* Filters: Search, ID, Status, and date range */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={2}
            gap={2}
            mt={2}
          >
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <TextField
                type="search"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder={t("call_center.search")}
              />

              <TextField
                type="search"
                size="small"
                value={id}
                onChange={(e) => setID(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder={t("call_center.searchID", "Search By ID")}
              />

              {/* Created At filter */}
              <TextField
                type="date"
                size="small"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                }}
              />

              {/* Status filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={statusID}
                  onChange={(e) => setStatusID(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">{t("call_center.all")}</MenuItem>
                  {status?.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Reset Filters */}
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setSearchTerm("");
                  setKeyword("");
                  setStatusID("");
                  setID("");
                  setCreatedAt("");
                  const today = new Date();
                  if (
                    permissions?.is_staff ||
                    permissions?.call_center_admin ||
                    permissions?.is_manager_in_call_center
                  ) {
                    const todayStr = today.toISOString().split("T")[0];
                    setStartCreatedAt(todayStr);
                    setEndCreatedAt(todayStr);
                  } else {
                    const startMonth = new Date(
                      today.getFullYear(),
                      today.getMonth(),
                      1
                    );
                    const endMonth = new Date(
                      today.getFullYear(),
                      today.getMonth() + 1,
                      0
                    );
                    setStartCreatedAt(startMonth.toISOString().split("T")[0]);
                    setEndCreatedAt(endMonth.toISOString().split("T")[0]);
                  }
                  setPage(1);
                }}
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "2px",
                  margin: "0px 3px",
                }}
              >
                {t("call_center.resetFilters")}
              </Button>
            </Box>
            <ExportToPDF
              data={exportData}
              columns={exportColumns}
              title={t("call_center.clientsList")}
            />
          </Box>

          <TableContainer sx={{ marginTop: 2, overflowY: "auto" }}>
            {isLoading ? (
              <Loading />
            ) : isError ? (
              <ErrorPage />
            ) : (
              <Table stickyHeader>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.id")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.name")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.contact")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.procedure")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.procedureDate")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.status")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.price")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.by")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.description")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.created_at", "Registration Date")}
                    </TableCell>
                    <TableCell style={tableCellStyle}>
                      {t("call_center.action")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                  {clients?.results?.map((client, index) => (
                    <TableRow key={index} hover style={getRowStyle(client)}>
                      <TableCell style={tableCellStyle}>{client.id}</TableCell>
                      <TableCell style={tableCellStyle}>
                        {client.full_name}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {client.contact}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", textAlign: "center" }}
                      >
                        {client?.display_procedure?.name}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {FormatDateTime(client?.procedure_date)}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        <Typography
                          fontWeight={"bold"}
                          border={"1px solid #d6d0d0"}
                          padding={"5px"}
                          borderRadius={"10px"}
                          textAlign={"center"}
                        >
                          {client?.status_info?.name}
                        </Typography>
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {client.price}
                      </TableCell>
                      <TableCell>
                        {client?.display_user?.first_name}{" "}
                        {client?.display_user?.last_name}
                      </TableCell>
                      <TableCell style={tableCellStyle}>
                        {client?.description?.length > 0 && (
                          <InfoIcon
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              handleOpenDialog();
                              setDescription(client?.description);
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell style={tableCellStyle}>
                        {FormatDateTime(client?.created_at)}
                      </TableCell>

                      <TableCell style={tableCellStyle}>
                        <Grid
                          container
                          justifyContent={"space-evenly"}
                          alignItems={"center"}
                        >
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            sx={{ textTransform: "capitalize" }}
                            endIcon={
                              <TrendingUpIcon sx={{ margin: "0px 5px" }} />
                            }
                            onClick={() => {
                              setOpenEdit(true);
                              setEditData(client);
                            }}
                          >
                            {t("call_center.follow")}
                          </Button>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Typography fontWeight={"bold"} sx={{ padding: "10px 0px" }}>
              Total: {total}
            </Typography>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={10}
            mb={5}
          >
            <Box display="flex" alignItems="center">
              <Typography>{t("call_center.rowsPerPage")}:</Typography>
              <FormControl
                sx={{
                  ml: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { border: "none" },
                  },
                }}
                size="small"
              >
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalClient / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
            />
          </Box>
        </Grid>
      </Paper>
      <Dialog maxWidth="xl" open={open} onClose={handleOpenDialog}>
        <DialogContent>
          <Typography variant="body">{description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOpenDialog}>{t("call_center.close")}</Button>
        </DialogActions>
      </Dialog>

      {openEdit && (
        <EditClient
          openEdit={openEdit}
          data={editData}
          onClose={() => {
            setOpenEdit(false);
          }}
        />
      )}
    </Grid>
  );
};

export default ClientList;
